import React, { useEffect, useMemo, useState } from 'react';
import { useGasI18n } from '../../shared/kit';
import { ActivityCard } from '../user-home/components/ActivityCard';
import { fetchArchiveYear, fetchCurrent } from './api';
import { agendaItemToCard, agendaOnlineItemToCard, agendaOnlineTeaserToCard } from './cardAdapter';
import { AgendaCompactView } from './components/AgendaCompactView';
import { AgendaCalendarView } from './components/AgendaCalendarView';
import { AgendaMapView } from './components/AgendaMapView';
import { AgendaViewSwitcher } from './components/AgendaViewSwitcher';
import type {
    AgendaInitData,
    AgendaItem,
    AgendaOnlineItem,
    AgendaReadModel,
    AgendaView,
    AgendaYear,
} from './types';

const SUPPORTED_VIEWS: AgendaView[] = ['grid', 'compact', 'month', 'calendar', 'map'];
/** Bandeau « Replays disponibles » : 2 cards max, tirage aléatoire. */
const REPLAY_TEASER_COUNT = 2;
const REPLAYS_ANCHOR_ID = 'gas-agenda-replays';

interface Props {
    initData: AgendaInitData;
}

/** Fisher–Yates (copie) — ordre aléatoire pour le bandeau. */
function shuffleOnline(items: AgendaOnlineItem[]): AgendaOnlineItem[] {
    const pool = [...items];
    for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = pool[i];
        pool[i] = pool[j]!;
        pool[j] = tmp!;
    }
    return pool;
}

function flattenYear(year: AgendaYear | undefined): AgendaItem[] {
    if (!year) {
        return [];
    }
    // Mois triés chronologiquement (clés "01"…"12"), puis items par date.
    const monthKeys = Object.keys(year.months).sort((a, b) => a.localeCompare(b));
    const items = monthKeys.flatMap((k) => year.months[k]?.items ?? []);
    return [...items].sort((a, b) => {
        const da = a.date_start || '';
        const db = b.date_start || '';
        if (da !== db) {
            return da < db ? -1 : 1;
        }
        return a.id - b.id;
    });
}

interface AgendaFilters {
    type: string;
    text: string;
    lieu: string;
    month: string; // '01'..'12'
}

function matchFilters(item: AgendaItem, f: AgendaFilters): boolean {
    const matchType = !f.type || item.type === f.type;
    const matchText = !f.text || item.title.toLowerCase().includes(f.text);
    const matchLieu = !f.lieu || item.lieu === f.lieu;
    const matchMonth = !f.month || (item.date_start || '').slice(5, 7) === f.month;
    return matchType && matchText && matchLieu && matchMonth;
}

export default function App({ initData }: Props): React.ReactElement {
    const { t } = useGasI18n();
    const { rest_base_url: restBaseUrl, locale, rest_nonce: restNonce } = initData;

    const [model, setModel] = useState<AgendaReadModel | null>(initData.initial ?? null);
    const [loading, setLoading] = useState(!initData.initial);
    const [error, setError] = useState(false);

    const currentYear = model?.current_year ?? new Date().getFullYear();
    const yearMinus1 = currentYear - 1;
    const yearMinus2 = currentYear - 2;

    const [activeTab, setActiveTab] = useState<string>(String(currentYear));
    const [filterType, setFilterType] = useState('');
    const [filterText, setFilterText] = useState('');
    const [filterLieu, setFilterLieu] = useState('');
    const [filterMonth, setFilterMonth] = useState('');

    // Facettes activées par la site-config agenda_display.available_filters
    // (Lot 0e, activite-evenement-billets). Défaut = comportement historique.
    const availableFilters = useMemo<string[]>(() => {
        const configured = (initData.display?.available_filters ?? ['type', 'text']).filter((f) => typeof f === 'string');
        return configured.length > 0 ? configured : ['type', 'text'];
    }, [initData.display]);

    // Modes d'affichage (Lot 3) : intersection config × vues supportées.
    const availableViews = useMemo<AgendaView[]>(() => {
        const configured = (initData.display?.available_views ?? ['grid']).filter((v) => SUPPORTED_VIEWS.includes(v));
        return configured.length > 0 ? configured : ['grid'];
    }, [initData.display]);
    const showPast = initData.display?.show_past !== false;
    /** Onglets d'années visibles (ex. 2026, 2025, 2024) — le reste passe en Archives. */
    const recentYearTabs = showPast
        ? [currentYear, yearMinus1, yearMinus2]
        : [currentYear];
    const [view, setView] = useState<AgendaView>(() => {
        const def = initData.display?.default_view ?? 'grid';
        return availableViews.includes(def) ? def : availableViews[0];
    });

    // Archives chargées à la demande (scope=years=YYYY) → cache local.
    const [archiveYears, setArchiveYears] = useState<Record<string, AgendaItem[]>>({});
    const [archiveSelected, setArchiveSelected] = useState('');
    const [archiveLoading, setArchiveLoading] = useState(false);

    useEffect(() => {
        // Toujours revalider via REST (credentials) : l'init PHP peut être une
        // version « anonyme » servie par cache full-page, et sans revalidation
        // un membre ne verrait jamais les archives logged_in/member.
        let cancelled = false;
        if (!initData.initial) {
            setLoading(true);
        }
        fetchCurrent(restBaseUrl, locale, restNonce)
            .then((data) => {
                if (!cancelled) {
                    setModel(data);
                    // Ne réinitialiser l'onglet qu'au premier chargement sans init.
                    if (!initData.initial) {
                        setActiveTab(String(data.current_year));
                    }
                    setError(false);
                }
            })
            .catch(() => {
                if (!cancelled && !initData.initial) {
                    setError(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [initData.initial, restBaseUrl, locale, restNonce]);

    const archiveYearList = useMemo(() => {
        if (!model) {
            return [] as Array<{ year: string; count: number }>;
        }
        // Archives = années < N-2 avec au moins 1 activité VISIBLE (count > 0).
        // Évite « 2022 (7) » fantôme quand publicOnly a vidé l'année.
        return Object.entries(model.year_counts)
            .map(([year, count]) => ({ year, count: Number(count) || 0 }))
            .filter((e) => Number(e.year) < yearMinus2 && e.count > 0)
            .sort((a, b) => Number(b.year) - Number(a.year));
    }, [model, yearMinus2]);

    // Valeurs de facette « lieu » : distincts non vides sur toutes les années
    // chargées (100 % client — le read-model est déjà en mémoire).
    const lieuOptions = useMemo<string[]>(() => {
        if (!model || !availableFilters.includes('lieu')) {
            return [];
        }
        const values = new Set<string>();
        Object.values(model.years).forEach((year) => {
            Object.values(year?.months ?? {}).forEach((m) => {
                (m.items ?? []).forEach((it) => {
                    const lieu = (it.lieu || '').trim();
                    if (lieu !== '') {
                        values.add(lieu);
                    }
                });
            });
        });
        (model.online ?? []).forEach((it) => {
            const lieu = (it.lieu || '').trim();
            if (lieu !== '') {
                values.add(lieu);
            }
        });
        return Array.from(values).sort((a, b) => a.localeCompare(b, locale || 'fr'));
    }, [model, availableFilters, locale]);

    const monthOptions = useMemo<Array<{ value: string; label: string }>>(() => {
        if (!availableFilters.includes('month')) {
            return [];
        }
        const fmt = new Intl.DateTimeFormat(locale || 'fr', { month: 'long' });
        return Array.from({ length: 12 }, (_, i) => ({
            value: String(i + 1).padStart(2, '0'),
            label: fmt.format(new Date(2026, i, 1)),
        }));
    }, [availableFilters, locale]);

    function handleSwitchTab(tab: string): void {
        setActiveTab(tab);
        setFilterType('');
        setFilterText('');
        setFilterLieu('');
        setFilterMonth('');
    }

    async function handleArchiveSelect(year: string): Promise<void> {
        setArchiveSelected(year);
        if (!year || archiveYears[year]) {
            return;
        }
        // Preferer les années déjà dans le read-model (scope=all / init PHP) —
        // le REST publicOnly ne contient que les fiches publiques, or les
        // archives 2022–… sont souvent logged_in/member uniquement.
        const fromModel = flattenYear(model?.years?.[year]);
        if (fromModel.length > 0) {
            setArchiveYears((prev) => ({ ...prev, [year]: fromModel }));
            return;
        }
        setArchiveLoading(true);
        try {
            const data = await fetchArchiveYear(restBaseUrl, locale, year, restNonce);
            setArchiveYears((prev) => ({
                ...prev,
                [year]: flattenYear(data.years?.[year] ?? data.years?.[String(year)]),
            }));
        } catch {
            setArchiveYears((prev) => ({ ...prev, [year]: [] }));
        } finally {
            setArchiveLoading(false);
        }
    }

    // Bandeau Replays : 2 cards aléatoires (re-tirage seulement si le catalogue change).
    const onlineIdsKey = (model?.online ?? []).map((it) => it.id).join(',');
    const replayTeaser = useMemo(() => {
        const list = model?.online ?? [];
        return shuffleOnline(list).slice(0, REPLAY_TEASER_COUNT);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- onlineIdsKey résume le catalogue
    }, [onlineIdsKey]);

    if (loading) {
        return <div className="gas-agenda__loading">{t('common.loading', {}, 'Chargement…')}</div>;
    }
    if (error || !model) {
        return <div className="gas-agenda__empty"><p>{t('errors.generic', {}, 'Une erreur est survenue.')}</p></div>;
    }

    const textLc = filterText.toLowerCase().trim();
    const activeFilters: AgendaFilters = { type: filterType, text: textLc, lieu: filterLieu, month: filterMonth };
    const renderItems = (items: AgendaItem[]): React.ReactElement => {
        const filtered = items.filter((it) => matchFilters(it, activeFilters));
        // Calendrier + par mois : toutes les dates (sessions enfants list_hidden incluses).
        // Cards / compact / carte : 1 entrée catalogue par parent (exclut list_hidden).
        const forList = (view === 'calendar' || view === 'month')
            ? filtered
            : filtered.filter((it) => !it.list_hidden);
        if (forList.length === 0) {
            return (
                <div className="gas-agenda__empty gas-agenda__empty--filtered">
                    <p>{t('agenda.empty_filtered', {}, 'Aucune activité ne correspond à vos critères.')}</p>
                </div>
            );
        }
        if (view === 'compact') {
            return <AgendaCompactView items={forList} locale={locale} t={t} />;
        }
        if (view === 'month') {
            return <AgendaCompactView items={forList} groupByMonth locale={locale} t={t} />;
        }
        if (view === 'calendar') {
            return <AgendaCalendarView items={forList} t={t} />;
        }
        if (view === 'map') {
            return (
                <AgendaMapView
                    items={forList}
                    t={t}
                    mapStyleUrl={initData.map_style_url}
                    mapStyleUrlDark={initData.map_style_url_dark}
                />
            );
        }
        return (
            <div className="gas-agenda__grid">
                {forList.map((it) => (
                    <div
                        key={it.id}
                        className="gas-agenda__card-wrapper"
                        data-type={it.type}
                        data-title={it.title.toLowerCase()}
                    >
                        <ActivityCard card={agendaItemToCard(it, t)} />
                    </div>
                ))}
            </div>
        );
    };
    // Cartes / liste / par mois : sections « à venir » puis « passées ».
    // Calendrier et carte : rendu combiné (toutes dates).
    const splitSections = view === 'grid' || view === 'compact' || view === 'month';

    const upcoming = model.upcoming ?? [];
    const currentYearItems = flattenYear(model.years[String(currentYear)]);
    // Vue « par mois » : inclure les sessions enfants (list_hidden) — le tableau
    // upcoming du read-model ne les contient pas (1 card catalogue = parent).
    const isItemUpcoming = (it: AgendaItem): boolean => {
        if (it.is_finished) {
            return false;
        }
        const start = it.date_start || '';
        if (!start) {
            return false;
        }
        const ts = new Date(start.replace(' ', 'T')).getTime();
        return !Number.isNaN(ts) && ts > Date.now();
    };
    const monthUpcoming = currentYearItems.filter((it) => isItemUpcoming(it));
    const monthPast = currentYearItems.filter((it) => !isItemUpcoming(it));
    const upcomingIds = new Set(upcoming.map((i) => i.id));
    const pastCurrentYear = currentYearItems.filter((i) => !upcomingIds.has(i.id) && !i.list_hidden);
    const yearMinus1Items = flattenYear(model.years[String(yearMinus1)]);
    const yearMinus2Items = flattenYear(model.years[String(yearMinus2)]);
    const online = model.online ?? [];

    const sectionUpcoming = view === 'month' ? monthUpcoming : upcoming;
    const sectionPast = view === 'month' ? monthPast : pastCurrentYear;

    const tabCount = (n: number): number => model.year_counts[String(n)] ?? 0;
    const archivesTotal = archiveYearList.reduce((sum, e) => sum + e.count, 0);

    /**
     * @param mode teaser = bandeau (léger, 2 cards) ; catalog = bas de page
     *             (même layout horizontal 2 cols, mais détails complets).
     */
    const renderOnlineGrid = (items: typeof online, mode: 'teaser' | 'catalog'): React.ReactElement => (
        <div className="gas-agenda__grid gas-agenda__grid--teaser">
            {items.map((it) => (
                <div
                    key={it.id}
                    className="gas-agenda__card-wrapper gas-agenda__card-wrapper--online gas-agenda__card-wrapper--teaser"
                    data-type={it.type}
                >
                    <ActivityCard
                        card={mode === 'teaser' ? agendaOnlineTeaserToCard(it, t) : agendaOnlineItemToCard(it, t)}
                        compact
                    />
                </div>
            ))}
        </div>
    );

    const scrollToReplays = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();
        const el = document.getElementById(REPLAYS_ANCHOR_ID);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Focus pour l'accessibilité (titre de section si possible)
            const heading = el.querySelector<HTMLElement>('h2, h3');
            heading?.focus({ preventScroll: true });
        }
        // Met à jour le hash sans saut brutal
        if (window.history?.replaceState) {
            window.history.replaceState(null, '', `#${REPLAYS_ANCHOR_ID}`);
        }
    };

    return (
        <div className="gas-agenda">
            {/* Hero */}
            <section className="gas-agenda__hero">
                {initData.is_user_member ? (
                    <div className="gas-agenda__hero-layout--member">
                        <div className="gas-agenda__hero-left">
                            <h1>{t('agenda.title', {}, 'Agenda des activités')}</h1>
                            <p>{t('agenda.intro_member', { name: initData.association_name }, `Formations, ateliers et événements de ${initData.association_name}.`)}</p>
                        </div>
                        {initData.webcal_url && (
                            <div className="gas-agenda__hero-right">
                                <div className="gas-agenda__calendar-box">
                                    <h4>{t('agenda.calendar.title', {}, 'Calendrier')}</h4>
                                    <a href={initData.webcal_url} className="gas-button gas-button--outline gas-button--small">
                                        {t('agenda.calendar.subscribe', {}, 'S’abonner')}
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="gas-agenda__hero-layout--visitor">
                        <h1>{t('agenda.title', {}, 'Agenda des activités')}</h1>
                        <p>{t('agenda.intro_visitor', {}, 'Découvrez toutes nos formations, ateliers et événements.')}</p>
                    </div>
                )}
            </section>

            {/* Bandeau Replays (option A) — toujours visible, au-dessus des onglets */}
            {replayTeaser.length > 0 && (
                <section className="gas-agenda__replay-teaser" aria-labelledby="gas-agenda-replay-teaser-title">
                    <div className="gas-agenda__replay-teaser-header">
                        <div className="gas-agenda__replay-teaser-intro">
                            <h2 id="gas-agenda-replay-teaser-title" className="gas-agenda__replay-teaser-title">
                                {t('agenda.teaser.online_title', {}, 'Replays disponibles')}
                            </h2>
                            <p className="gas-agenda__replay-teaser-hint">
                                {t(
                                    'agenda.teaser.online_hint',
                                    {},
                                    'Formations et ateliers déjà tenus, à suivre en vidéo à votre rythme.',
                                )}
                            </p>
                        </div>
                        <a
                            href={`#${REPLAYS_ANCHOR_ID}`}
                            className="gas-button gas-button--primary-outline gas-button--small gas-agenda__replay-teaser-link"
                            onClick={scrollToReplays}
                        >
                            {t(
                                'agenda.teaser.see_all',
                                { count: online.length },
                                `Voir tous les replays (${online.length})`,
                            )}
                        </a>
                    </div>
                    {renderOnlineGrid(replayTeaser, 'teaser')}
                </section>
            )}

            {/* Tabs : 2026 / 2025 / 2024 / Archives (plus d'onglet Replays) */}
            <nav className="gas-agenda__tabs" role="tablist">
                {recentYearTabs.map((year) => (
                    <button
                        key={year}
                        type="button"
                        role="tab"
                        className={`gas-agenda__tab${activeTab === String(year) ? ' gas-agenda__tab--active' : ''}`}
                        aria-selected={activeTab === String(year)}
                        onClick={() => handleSwitchTab(String(year))}
                    >
                        {year}
                        <span className="gas-agenda__tab-count">{tabCount(year)}</span>
                    </button>
                ))}
                {showPast && archiveYearList.length > 0 && (
                    <button
                        type="button"
                        role="tab"
                        className={`gas-agenda__tab${activeTab === 'archives' ? ' gas-agenda__tab--active' : ''}`}
                        aria-selected={activeTab === 'archives'}
                        onClick={() => handleSwitchTab('archives')}
                    >
                        {t('agenda.tab.archives', {}, 'Archives')}
                        <span className="gas-agenda__tab-count">{archivesTotal}</span>
                    </button>
                )}
            </nav>

            {/* Filtres + sélecteur de vue */}
            <div className="gas-agenda__filters">
                {availableFilters.includes('type') && (
                    <div className="gas-agenda__filter-group">
                        <label htmlFor="gas-agenda-filter-type">{t('agenda.filter.type', {}, 'Type')}</label>
                        <select
                            id="gas-agenda-filter-type"
                            className="gas-agenda__filter-type"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                        >
                            <option value="">{t('agenda.filter.all', {}, 'Tous')}</option>
                            {Object.entries(model.types).map(([slug, label]) => (
                                <option key={slug} value={slug}>{label}</option>
                            ))}
                        </select>
                    </div>
                )}
                {lieuOptions.length > 0 && (
                    <div className="gas-agenda__filter-group">
                        <label htmlFor="gas-agenda-filter-lieu">{t('agenda.filter.lieu', {}, 'Lieu')}</label>
                        <select
                            id="gas-agenda-filter-lieu"
                            className="gas-agenda__filter-lieu"
                            value={filterLieu}
                            onChange={(e) => setFilterLieu(e.target.value)}
                        >
                            <option value="">{t('agenda.filter.all_lieux', {}, 'Tous les lieux')}</option>
                            {lieuOptions.map((lieu) => (
                                <option key={lieu} value={lieu}>{lieu}</option>
                            ))}
                        </select>
                    </div>
                )}
                {monthOptions.length > 0 && (
                    <div className="gas-agenda__filter-group">
                        <label htmlFor="gas-agenda-filter-month">{t('agenda.filter.month', {}, 'Mois')}</label>
                        <select
                            id="gas-agenda-filter-month"
                            className="gas-agenda__filter-month"
                            value={filterMonth}
                            onChange={(e) => setFilterMonth(e.target.value)}
                        >
                            <option value="">{t('agenda.filter.all_months', {}, 'Tous les mois')}</option>
                            {monthOptions.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                    </div>
                )}
                {availableFilters.includes('text') && (
                    <div className="gas-agenda__filter-group">
                        <label htmlFor="gas-agenda-filter-search">{t('common.search', {}, 'Recherche')}</label>
                        <input
                            id="gas-agenda-filter-search"
                            type="text"
                            className="gas-agenda__filter-search"
                            value={filterText}
                            placeholder={t('agenda.filter.search_placeholder', {}, 'Rechercher une activité…')}
                            onChange={(e) => setFilterText(e.target.value)}
                        />
                    </div>
                )}
                <AgendaViewSwitcher views={availableViews} active={view} onChange={setView} t={t} />
            </div>

            {/* Panel année courante — agenda pur (plus de grille Replays au milieu) */}
            {activeTab === String(currentYear) && (
                <div className="gas-agenda__panel" data-panel={currentYear}>
                    {splitSections ? (
                        <>
                            {sectionUpcoming.length > 0 && (
                                <>
                                    <h3 className="gas-agenda__section-title">{t('agenda.section.upcoming', {}, 'Activités à venir')}</h3>
                                    {renderItems(sectionUpcoming)}
                                </>
                            )}
                            {showPast && sectionPast.length > 0 && (
                                <>
                                    <h3 className="gas-agenda__section-title">{t('agenda.section.past_year', { year: currentYear }, `Activités passées ${currentYear}`)}</h3>
                                    {renderItems(sectionPast)}
                                </>
                            )}
                        </>
                    ) : (
                        renderItems(showPast ? currentYearItems : upcoming)
                    )}
                </div>
            )}

            {/* Panel année-1 (ex. 2025) */}
            {showPast && activeTab === String(yearMinus1) && (
                <div className="gas-agenda__panel" data-panel={yearMinus1}>
                    {renderItems(yearMinus1Items)}
                </div>
            )}

            {/* Panel année-2 (ex. 2024) */}
            {showPast && activeTab === String(yearMinus2) && (
                <div className="gas-agenda__panel" data-panel={yearMinus2}>
                    {renderItems(yearMinus2Items)}
                </div>
            )}

            {/* Panel archives */}
            {activeTab === 'archives' && (
                <div className="gas-agenda__panel" data-panel="archives">
                    <div className="gas-agenda__archives-controls">
                        <label htmlFor="gas-agenda-archive-year">{t('agenda.archives.choose_year', {}, 'Choisir une année')}</label>
                        <select
                            id="gas-agenda-archive-year"
                            className="gas-agenda__archives-select"
                            value={archiveSelected}
                            onChange={(e) => handleArchiveSelect(e.target.value)}
                        >
                            <option value="">{t('agenda.archives.select_prompt', {}, '— Sélectionnez —')}</option>
                            {archiveYearList.map((e) => (
                                <option key={e.year} value={e.year}>{`${e.year} (${e.count})`}</option>
                            ))}
                        </select>
                    </div>
                    {archiveLoading && (
                        <div className="gas-agenda__archives-loading">{t('common.loading', {}, 'Chargement…')}</div>
                    )}
                    {!archiveLoading && archiveSelected && (
                        <div className="gas-agenda__archives-content">
                            {(archiveYears[archiveSelected] ?? []).length > 0
                                ? renderItems(archiveYears[archiveSelected] ?? [])
                                : (
                                    <div className="gas-agenda__empty">
                                        <p>{t('agenda.archives.empty_year', {}, 'Aucune activité visible pour cette année.')}</p>
                                    </div>
                                )}
                        </div>
                    )}
                </div>
            )}

            {/* Catalogue Replays complet — ancre cible du bandeau « Voir tous » */}
            {online.length > 0 && (
                <section
                    id={REPLAYS_ANCHOR_ID}
                    className="gas-agenda__replays-catalog"
                    aria-labelledby="gas-agenda-replays-title"
                >
                    <h2
                        id="gas-agenda-replays-title"
                        className="gas-agenda__section-title"
                        tabIndex={-1}
                    >
                        {t('agenda.section.online', {}, 'Replays à la vente')}
                    </h2>
                    <p className="gas-agenda__section-subtitle">
                        {t(
                            'agenda.section.online_hint',
                            {},
                            'Ces formations et ateliers ont déjà eu lieu. Vous pouvez les acheter en version replay vidéo pour les suivre à votre rythme.',
                        )}
                    </p>
                    {renderOnlineGrid(online, 'catalog')}
                </section>
            )}

            {/* CTA contextuel */}
            {!initData.is_user_member && initData.become_member_url && (
                <div className="gas-agenda__cta">
                    <div className="gas-agenda__cta-content">
                        <div className="gas-agenda__cta-text">
                            <h3>{t('agenda.cta.join_title', {}, 'Rejoignez notre communauté')}</h3>
                        </div>
                        <div className="gas-agenda__cta-action">
                            <a href={initData.become_member_url} className="gas-button gas-button--primary">
                                {t('agenda.cta.become_member', {}, 'Devenir membre')}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
